import { z } from "zod";
import { TEMPLATE_INDUSTRY_LIMIT } from "./catalog-taxonomy.js";
import { homeProviderFor, modelRefIssue, parseModelRef } from "./models.js";
import { MultiagentSchema } from "./multiagent.js";
import { AgentOutcomeSchema } from "./outcome.js";
import { DELIVERIES } from "./seat-delivery.js";
import { LocaleSchema, SiteIdSchema } from "./sites.js";
import { ContentKeySchema } from "./wire.js";
/**
 * The catalog's one addition to the MultiAgent-YAML layout (workflows/V5-Plan.v2.md §2):
 * `catalog.yaml` beside each `agent.yaml` carries placement and copy — never behaviour —
 * plus the two session-level things the agent YAML has no place for, the goal (the first
 * `user.message`) and the outcome (`user.define_outcome` at session start).
 */
export const CATALOG_FORMAT = "agents.myfirstagent.ai/v2";
export const DeliverableSchema = z.strictObject({
    /** Relative deliverable name (never a provider path such as /mnt/…). */
    name: z
        .string()
        .regex(/^[\w.-]+(\/[\w.-]+)*$/)
        .max(200),
    description: z.string().max(200).optional(),
});
export const CatalogOutcomeSchema = AgentOutcomeSchema.extend({
    deliverables: z.array(DeliverableSchema).max(10).default([]),
});
export const CatalogTranslationSchema = z.strictObject({
    title: z.string().min(1).max(160),
    description: z.string().max(500),
});
/**
 * A translation as `catalog.yaml` authors it: the authored locale's title may be left out, and
 * is then agent.yaml's `name` (the template-method review, item 23). Every other locale names
 * its own title.
 */
export const CatalogSidecarTranslationSchema = z.strictObject({
    title: CatalogTranslationSchema.shape.title.optional(),
    description: CatalogTranslationSchema.shape.description,
});
export const CatalogSidecarSchema = z
    .strictObject({
    /**
     * Where the template sits: `content/agents/<site>/<library>/<key>/`. Each is derived from
     * the folder when left out and must equal it when given (`template_placement_mismatch`),
     * so the lines are redundant and optional (the template-method review, item 23).
     */
    key: ContentKeySchema.optional(),
    site: SiteIdSchema.optional(),
    library: z.string().min(1).max(80).optional(),
    authoredLocale: LocaleSchema,
    /**
     * A key of `categories` in content/taxonomy.yaml: the template's topic. Optional here
     * because stored versions may lack it; the content gate requires it (`category_missing`).
     */
    category: z.string().max(80).optional(),
    /**
     * The keywords: keys of `keywords` in content/taxonomy.yaml, the collections the template
     * belongs to (`original` among them).
     */
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    /**
     * Whose work it serves: keys of `industries` in content/taxonomy.yaml; `general` only alone.
     * The content gate requires 1 to 3 for every template (`industries_missing`).
     */
    industries: z.array(ContentKeySchema).max(TEMPLATE_INDUSTRY_LIMIT).default([]),
    /**
     * @deprecated Nothing reads it (Rev 2 §R2.8). Still accepted because stored versions
     * (`template_versions.manifest`) carry it and must keep parsing; a later PR drops it.
     */
    audienceBands: z.array(z.string().min(1)).max(10).default([]),
    translations: z.partialRecord(LocaleSchema, CatalogSidecarTranslationSchema),
    goal: z.string().max(2000).optional(),
    outcome: CatalogOutcomeSchema.optional(),
})
    .superRefine((sidecar, context) => {
    if (!sidecar.translations[sidecar.authoredLocale]) {
        context.addIssue({
            code: "custom",
            path: ["translations"],
            message: `translations must include the authored locale ${sidecar.authoredLocale}`,
        });
    }
    for (const [locale, translation] of Object.entries(sidecar.translations)) {
        if (locale !== sidecar.authoredLocale && translation && translation.title === undefined) {
            context.addIssue({
                code: "custom",
                path: ["translations", locale, "title"],
                message: `translations.${locale} needs a title: only the authored locale's title defaults to agent.yaml's name`,
            });
        }
    }
});
/**
 * The sidecar with what it may leave out filled in: key, site and library from its folder, and
 * the authored locale's title from agent.yaml's `name`. What the sidecar does give is kept as
 * written (the gate compares a given placement with the folder).
 */
export function resolveCatalogSidecar(source, placement, agentName) {
    const translations = {};
    for (const [locale, translation] of Object.entries(source.translations)) {
        if (!translation)
            continue;
        // The schema refuses an untitled translation in any other locale.
        translations[locale] = {
            title: translation.title ?? agentName,
            description: translation.description,
        };
    }
    return {
        ...source,
        key: source.key ?? placement.key,
        site: source.site ?? placement.site,
        library: source.library ?? placement.library,
        translations,
    };
}
/**
 * The fields of `agent.yaml` the catalog itself reads. The spec checker validates the
 * whole document (`v5/scripts/spec`); the catalog only needs the name, the model (which
 * decides the engine), the prompt it lists, and the roster for the cross-tree checks.
 */
export const CatalogAgentSchema = z.looseObject({
    name: z.string().min(1).max(256),
    description: z.string().max(2048).optional(),
    model: z.union([
        z
            .string()
            .min(1)
            .refine((id) => modelRefIssue(id) === null, {
            error: (issue) => modelRefIssue(String(issue.input)) ?? "not a model id",
        }),
        z.looseObject({
            id: z
                .string()
                .min(1)
                .refine((id) => modelRefIssue(id) === null, {
                error: (issue) => modelRefIssue(String(issue.input)) ?? "not a model id",
            }),
        }),
    ]),
    system: z.string().min(1).max(100_000),
    multiagent: MultiagentSchema.nullable().optional(),
});
export const ENGINES = ["cma_native", "foundry_responses", "openrouter_responses"];
/**
 * The runners a run or a project may ask for by name (the service-routing plan §3.3a): the
 * loop or the service. Anthropic is never asked for; it is where an Anthropic-provider run
 * goes. An explicit choice the runner cannot serve is refused (`runner_unsupported`), never
 * silently replaced; only the automatic rule falls back, and records why.
 */
export const RUNNER_PINS = ["loop", "service"];
/** The runner a provider's runs take when nothing names one: Anthropic its own, the rest the loop. */
export const defaultRunnerFor = (provider) => provider === "anthropic" ? "anthropic" : "loop";
export const REPORT_PROVIDERS = ["claude_managed_agents", "foundry", "openrouter"];
export const agentModelId = (agent) => typeof agent.model === "string" ? agent.model : agent.model.id;
export const engineForProvider = (provider) => provider === "anthropic"
    ? "cma_native"
    : provider === "openrouter"
        ? "openrouter_responses"
        : "foundry_responses";
export const providerForEngine = (engine) => engine === "cma_native"
    ? "anthropic"
    : engine === "openrouter_responses"
        ? "openrouter"
        : "foundry";
/**
 * The engine a model runs on. A `<provider>/<name>` prefix names it outright, whether the name is
 * a catalog id or that provider's native name (owner decisions 2026-10-12). A bare id follows its home provider: Anthropic models run on Claude Managed
 * Agents, OpenRouter-home models on the stateless Responses chassis, everything else on Foundry
 * — with one authored exception: an overlay that maps an OpenRouter-home model to a Foundry
 * DEPLOYMENT pins Foundry (`mappedDeployment`; the shipped templates on Kimi and GLM were
 * written that way before the OpenRouter engine existed). An id the catalog does not know keeps
 * the folder's original rule (F3:55-250): Claude by name, Foundry otherwise.
 *
 * A template chooses OpenRouter with `openrouter/<id>` on its model line. An overlay mapping's
 * `openrouter: <catalog id>` seat says what each agent (the root included) runs as THERE; it
 * never picks the engine, so the pin above is not widened: a seat is per agent, an engine per run.
 */
export const engineForModel = (modelId, mappedDeployment = null) => {
    const ref = parseModelRef(modelId);
    if (ref?.provider)
        return engineForProvider(ref.provider);
    const canonical = ref?.canonical ?? modelId;
    const home = homeProviderFor(canonical);
    if (home === "anthropic")
        return "cma_native";
    if (home === "openrouter")
        return mappedDeployment ? "foundry_responses" : "openrouter_responses";
    if (home === "foundry")
        return "foundry_responses";
    return /^claude/i.test(canonical) ? "cma_native" : "foundry_responses";
};
/** The deployment an overlay (parsed YAML of any shape) maps `modelId` to, or null. Overlay keys are bare ids; a prefixed id is looked up by the catalog id it resolves to (a native name included). Only `.deployment` is read: an `openrouter` seat alone pins nothing. */
export function overlayDeploymentFor(overlay, modelId) {
    const models = overlay && typeof overlay === "object" && !Array.isArray(overlay)
        ? overlay.models
        : undefined;
    const canonical = parseModelRef(modelId)?.canonical ?? modelId;
    const mapping = models && typeof models === "object" && !Array.isArray(models)
        ? (models[canonical] ??
            models[modelId])
        : undefined;
    const deployment = mapping && typeof mapping === "object" && !Array.isArray(mapping)
        ? mapping.deployment
        : undefined;
    return typeof deployment === "string" && deployment.length > 0 ? deployment : null;
}
export const reportProviderFor = (engine) => engine === "cma_native"
    ? "claude_managed_agents"
    : engine === "openrouter_responses"
        ? "openrouter"
        : "foundry";
/**
 * The checker's report file for an engine and a runner (the service-routing plan §3.3b): a
 * provider with two runners writes `<provider>.<runner>.report.json`; Anthropic, whose runner
 * is fixed, keeps `<provider>.report.json`, and so does Foundry while its work is held on the
 * loop (microVM plan decision 24). The catalog's gate reads the loop's file, the path every
 * run of the template can take.
 */
export const reportFileFor = (engine, runner = "loop") => {
    const provider = reportProviderFor(engine);
    return engine === "openrouter_responses"
        ? `${provider}.${runner}.report.json`
        : `${provider}.report.json`;
};
/**
 * The runner a project's overlay pins (`execution.runner`, V5's own file: never agent.yaml,
 * which the strict checker and Anthropic read, nor catalog.yaml, which a project leaves
 * behind), or null. Read loosely like `overlayDeploymentFor`: the overlay may be of any shape.
 */
export function overlayRunnerFor(overlay) {
    const execution = overlay && typeof overlay === "object" && !Array.isArray(overlay)
        ? overlay.execution
        : undefined;
    const runner = execution && typeof execution === "object" && !Array.isArray(execution)
        ? execution.runner
        : undefined;
    return runner === "loop" || runner === "service" ? runner : null;
}
const SHA256_HEX = /^[a-f0-9]{64}$/;
/**
 * The checker's report as the catalog reads it (`reports/<provider>.report.json`). Its
 * freshness key is every input the checker read: agent.yaml (`specSha256`), the overlay
 * (`overlaySha256`, null without one), the sidecar's `outcome` block (`outcomeSha256`, written
 * only when the sidecar has one) and each roster member's agent.yaml (`memberSpecSha256`, by
 * roster id, written only for a team). The gate refuses a report whose key differs from the
 * tree's as `report_stale`; an absent field reads as "none".
 */
export const CatalogReportSchema = z.looseObject({
    agent: z.string(),
    provider: z.enum(REPORT_PROVIDERS),
    engine: z.enum(ENGINES).nullable(),
    verdict: z.enum(["ok", "degraded", "blocked"]),
    specSha256: z.string().regex(SHA256_HEX),
    overlaySha256: z.string().nullable(),
    /** sha256 of the canonical JSON of catalog.yaml's `outcome` (contentChecksum); absent = no outcome. */
    outcomeSha256: z.string().regex(SHA256_HEX).optional(),
    /** Roster id → sha256 of that member's agent.yaml; absent = no members. */
    memberSpecSha256: z.record(z.string(), z.string().regex(SHA256_HEX)).optional(),
    generatedAt: z.string(),
    findings: z.array(z.looseObject({
        field: z.string(),
        feature: z.string(),
        status: z.string(),
        policy: z.string(),
        hardFail: z.boolean(),
        note: z.string().optional(),
        /** How the seat gets the capability (seat-delivery.ts); absent on reports written before A1. */
        delivery: z.enum(DELIVERIES).optional(),
    })),
    requiredSpikes: z.array(z.string()).default([]),
});
//# sourceMappingURL=catalog-sidecar.js.map